/**
 * Section schema for the landing page.
 *
 * Each campaign stores a `sections` array of `{id, type, enabled, ...fields}`.
 * The order of items in this array defines the order on the landing page.
 *
 * If a campaign has no `sections` (legacy data), `mergeSections()` returns the
 * default set in the canonical order — so behaviour is identical to pre-feature.
 *
 * `hero` is locked: it carries the lead form. It is always rendered first and
 * cannot be removed or moved by the editor (UI enforces this, backend stays open).
 */

export const DEFAULT_SECTIONS = [
  {
    id: "hero",
    type: "hero",
    enabled: true,
    locked: true,
    eyebrow: "✦ הקהילה הישראלית להדרכה פיננסית",
    form_note: 'פרטים מאובטחים, ללא ספאם. בלחיצה את/ה מאשר/ת את ה<a class="underline" href="#footer">מדיניות</a>.',
  },
  {
    id: "pain",
    type: "pain",
    enabled: true,
    eyebrow: "מכירים את זה ש…",
    title: "ההצלחה החיצונית — והחרדה השקטה מבפנים.",
  },
  {
    id: "explanation",
    type: "explanation",
    enabled: true,
    title_main: "החיים יביאו אתכם תמיד לאותה נקודה.",
    title_accent: "עד שתבינו את הנקודה.",
    body: "האמת היא שאתם לא אשמים. אתם עובדים, מרוויחים מעל הממוצע ומשלמים הכל בזמן. מבחינת הבנקים אתם **\"לקוחות טובים\"**. אבל בפועל, הכסף נכנס ונעלם, חלק גדול ממנו הולך לריביות והחזרים, ובסוף החודש נשאר הרבה פחות ממה שציפיתם.",
  },
  {
    id: "questionnaire",
    type: "questionnaire",
    enabled: true,
    eyebrow: "השאלון",
    title: "בדיוק בשביל זה יצרתי את השאלון.",
    intro: "בדיקה פשוטה שתעזור לכם להבין מה באמת קורה לכסף שלכם בכל חודש. בכמה שאלות קצרות, בשפה פשוטה ובלי הרצאות על \"התנהלות נכונה\", תוכלו:",
    bullets: [
      "להבין מה באמת חונק לכם את התקציב החודשי",
      "לאן הכסף נעלם עוד לפני שהספקתם להשתמש בו",
      "אילו החזרים הפכו לנטל קבוע על החיים שלכם",
    ],
    sidebar_title: "למה הנכס שלכם הוא גלגל הצלה",
    sidebar_text: "איך דירה שכבר יש לכם יכולה להיות חלק מהפתרון ולא רק עוד משהו ששואב מכם כספים. איחוד הלוואות תחת המשכנתה יכול להחזיר לכם בין 1,000 ל-2,500 ש\"ח בכל חודש.",
  },
  {
    id: "stats",
    type: "stats",
    enabled: true,
  },
  {
    id: "bonus",
    type: "bonus",
    enabled: true,
    badge: "בונוס",
    title_prefix: "גישה לקהילת הוואטסאפ הסגורה:",
    title_accent: "אמת או חובות",
    body: "לעשות סדר בכסף זה לא משהו שאמורים לעבור לבד, מול האפליקציה של הבנק, בשתיים בלילה. בקהילה נשתף מקרי בוחן אמיתיים, טיפים פשוטים להבנה, ותשובות לשאלות שחוזרות אצל משפחות בדיוק כמוכם.",
    chips: ["✓ קהילה תומכת", "✓ טיפים שבועיים", "✓ ליווי אישי"],
  },
  {
    id: "final_cta",
    type: "final_cta",
    enabled: true,
    title: "הגיע הזמן לעצור את המרדף החודשי.",
    subtitle: "להבין לאן הכסף באמת הולך, ולהתחיל לבנות חודש שאפשר לנשום בו.",
  },

  // ------------------------------------------------------------------
  // Extended section catalogue — added for the "hidden-debts" campaign
  // and reusable by any future campaign. Disabled by default so existing
  // campaigns without explicit stored `sections` keep their behaviour.
  // ------------------------------------------------------------------
  {
    id: "story_outside",
    type: "narrative",
    enabled: false,
    eyebrow: "",
    title: "מבחוץ הכול נראה בסדר.",
    paragraphs: [],
    punch: "",
  },
  {
    id: "story_not_failure",
    type: "narrative",
    enabled: false,
    eyebrow: "",
    title: "אבל אתם לא כישלון!",
    subtitle: "פשוט אף אחד לא לימד אתכם לראות את התמונה המלאה",
    paragraphs: [],
    punch: "",
  },
  {
    id: "brand_intro",
    type: "brand_intro",
    enabled: false,
    eyebrow: "",
    title: "ברוכים הבאים ל״אמת או חובות״",
    body: "",
    chips: [],
    closing_body: "",
  },
  {
    id: "benefits",
    type: "benefits_cards",
    enabled: false,
    eyebrow: "",
    title: "",
    cards: [],
    cta_text: "יאללה, בואו נבדוק!",
    cta_note: "",
  },
  {
    id: "wont_find",
    type: "wont_find",
    enabled: false,
    eyebrow: "",
    title: "אבל רגע, חשוב לנו גם שתדעו מה לא תמצאו כאן",
    bullets: [],
    closing: "",
  },
  {
    id: "brand_story",
    type: "narrative",
    enabled: false,
    eyebrow: "סיפור המותג של ״אמת או חובות״",
    title: "הקהילה הזאת התחילה מרגע אחד שבו אנשים הפסיקו להתבייש.",
    paragraphs: [],
    punch: "",
  },
  {
    id: "aspirations",
    type: "aspirations",
    enabled: false,
    eyebrow: "",
    title: "מה אתם דוחים ליום ש״תסתדרו״?",
    bullets: [],
    goal_text: "",
  },
  {
    id: "how_it_works",
    type: "how_it_works",
    enabled: false,
    eyebrow: "וזה עובד הכי פשוט שיש!",
    title: "6 שאלות קצרות — תמונה ברורה אחת",
    steps: [],
    closing: "",
  },
  {
    id: "faq",
    type: "faq",
    enabled: false,
    eyebrow: "",
    title: "שאלות בבקשה…",
    items: [],
  },
  {
    id: "closing_slogan",
    type: "closing_slogan",
    enabled: false,
    title: "",
    subtitle: "",
  },
];

export const SECTION_LABELS = {
  hero: "Hero + טופס",
  pain: "בולטי כאב",
  explanation: "פסקת הסבר",
  questionnaire: "אינטרו לשאלון",
  stats: "סטטיסטיקות",
  bonus: "WhatsApp Community",
  final_cta: "CTA תחתון",
  // extended
  story_outside: "נרטיב: מבחוץ הכול בסדר",
  story_not_failure: "נרטיב: אתם לא כישלון",
  brand_intro: "אינטרו למותג",
  benefits: "3 יתרונות (כרטיסים)",
  wont_find: "מה לא תמצאו כאן",
  brand_story: "סיפור המותג",
  aspirations: "מה אתם דוחים",
  how_it_works: "איך זה עובד (3 שלבים)",
  faq: "שאלות ותשובות",
  closing_slogan: "סלוגן סוגר",
};

/**
 * Merge campaign-stored sections with defaults.
 *  - If campaign.sections is empty/missing → return DEFAULT_SECTIONS untouched
 *  - If present → for each stored item, look up the default of the same `id` and
 *    apply stored field overrides on top. Any default sections not present in the
 *    stored array are appended at the end (so adding a new section type via code
 *    deploys without requiring a manual edit).
 *  - `hero` is forced to first position regardless.
 */
export function mergeSections(stored) {
  if (!Array.isArray(stored) || stored.length === 0) {
    return DEFAULT_SECTIONS.map((s) => ({ ...s }));
  }
  const byId = Object.fromEntries(DEFAULT_SECTIONS.map((s) => [s.id, s]));
  const usedIds = new Set();
  const ordered = [];
  stored.forEach((item) => {
    if (!item || !item.id) return;
    const def = byId[item.id];
    if (!def) return; // unknown section type → skip
    ordered.push({ ...def, ...item });
    usedIds.add(item.id);
  });
  // Append any default sections that are missing from stored
  DEFAULT_SECTIONS.forEach((def) => {
    if (!usedIds.has(def.id)) ordered.push({ ...def });
  });
  // Force hero to be first
  const heroIdx = ordered.findIndex((s) => s.id === "hero");
  if (heroIdx > 0) {
    const [hero] = ordered.splice(heroIdx, 1);
    ordered.unshift(hero);
  }
  return ordered;
}
